import { ACCOUNTS_NAV_ITEMS } from '@constants/Accounts'
import { ISettings, ISettingsItem } from '@dataTypes/settings.type'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface AccountsHooks {
  activeItem: ISettingsItem | null
  expandedItems: string[]
  filteredNavItems: ISettings[]
  handleClearSearch: () => void
  handleSearch: (e: React.ChangeEvent<HTMLInputElement>) => void
  headerContextValue: {
    setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
    setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
    setShowSidebar: Dispatch<SetStateAction<boolean>>
  }
  headerButtons: ReactNode | null
  headerTitle: ReactNode | null
  onSubItemClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    item: ISettingsItem,
  ) => void
  searchQuery: string
  showSidebar: boolean
  toggleAllItems: (e: React.MouseEvent<HTMLButtonElement>) => void
  toggleItem: (e: React.MouseEvent<HTMLButtonElement>, itemId: string) => void
}

const useAccounts = (): AccountsHooks => {
  const [expandedItems, setExpandedItems] = useState<string[]>(() =>
    ACCOUNTS_NAV_ITEMS.map((item) => item.id),
  )
  const [activeItem, setActiveItem] = useState<ISettingsItem | null>(
    () => ACCOUNTS_NAV_ITEMS[0]?.items[0] ?? null,
  )
  const [headerButtons, setHeaderButtons] = useState<ReactNode | null>(null)
  const [customHeaderTitle, setCustomHeaderTitle] = useState<ReactNode | null>(
    null,
  )
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [showSidebar, setShowSidebar] = useState<boolean>(true)

  const headerContextValue = useMemo(
    () => ({
      setHeaderButtons,
      setHeaderTitle: setCustomHeaderTitle,
      setShowSidebar,
    }),
    [],
  )

  const filteredNavItems = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) {
      return ACCOUNTS_NAV_ITEMS
    }

    return ACCOUNTS_NAV_ITEMS.filter((item) => {
      const matchesItem = item.name.toLowerCase().includes(normalizedQuery)
      const matchesSubItem = item.items.some((subItem) =>
        subItem.name.toLowerCase().includes(normalizedQuery),
      )
      return matchesItem || matchesSubItem
    })
  }, [searchQuery])

  useEffect(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) {
      return
    }

    const matchedParentIds = ACCOUNTS_NAV_ITEMS.filter((item) =>
      item.items.some((subItem) =>
        subItem.name.toLowerCase().includes(normalizedQuery),
      ),
    ).map((item) => item.id)

    if (matchedParentIds.length > 0) {
      setExpandedItems((prev) =>
        Array.from(new Set([...prev, ...matchedParentIds])),
      )
    }
  }, [searchQuery])

  const handleClearSearch = (): void => {
    setSearchQuery('')
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value)
  }

  const toggleAllItems = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (expandedItems.length > 0) {
      setExpandedItems([])
    } else {
      setExpandedItems(ACCOUNTS_NAV_ITEMS.map((item) => item.id))
    }
  }

  const toggleItem = (
    e: React.MouseEvent<HTMLButtonElement>,
    itemId: string,
  ): void => {
    e.preventDefault()
    e.stopPropagation()
    const navItem = ACCOUNTS_NAV_ITEMS.find((item) => item.id === itemId)
    if (navItem && navItem.items.length === 0) {
      return
    }
    if (expandedItems.includes(itemId)) {
      setExpandedItems(expandedItems.filter((id) => id !== itemId))
    } else {
      setExpandedItems([...expandedItems, itemId])
    }
  }

  const onSubItemClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    item: ISettingsItem,
  ): void => {
    e.preventDefault()
    e.stopPropagation()
    setActiveItem(item)
  }

  const headerTitle = customHeaderTitle ?? activeItem?.name ?? null

  return {
    activeItem,
    expandedItems,
    filteredNavItems,
    handleClearSearch,
    handleSearch,
    headerContextValue,
    headerButtons,
    headerTitle,
    onSubItemClick,
    searchQuery,
    showSidebar,
    toggleAllItems,
    toggleItem,
  }
}

export default useAccounts
